import React, { useState } from 'react';
import { FiltersIcon } from '../../assets/images/components/NudosComponents';
import { BordOneToneIcon, NudosSelectButton, NudosSearchBar } from '../NudosComponents';
import InfoIcon from '../../assets/images/catalogue/infoIcon';
import { ICountryDetail } from '../../types/countries';
import Sort from './components/Sort/Sort';
import { ISortFilter } from '../../types/global';
import NudosRadioButton from '../NudosRadioButton/NudosRadioButton';
import './ModuleFilters.scss';

const DEFAULT_SEARCH_BAR_CLASS_NAMES = {
  selectContainer: 'filterSearchContainer',
  searchBarContainer: 'searchBarContainerCustom',
  searchBar: 'filterSearchBar',
  input: 'filterSearchInput'
};

const ModuleFiltersExpandableSelect = ({
  filterTitle,
  openSelectStatus,
  openSelectFunction,
  rawSelectOptions,
  handleChangeSelectedOption,
  isFilterActive,
  conditionedFilter,
  filterUnlocked,
  aditionalInformation,
  countryListAlias,
  optionTextCustomClass = '',
  showSort,
  onSortChange,
  showSortInFilter,
  customHeightContainer,
  showSearch,
  searchPlaceholder = 'Buscar...',
  sortAlphabetically,
  isLargeFilter,
  showReferenceLetters,
  titleFilterContainer,
  customSortClassName = '',
  useRadioButtons,
  currentSortFilter,
  searchBarClassNames,
  showBubbleInformation,
  optionContainerCustomClass = ''
}: IModuleFiltersExpandableSelect) => {
  const searchBarClasses = {
    selectContainer: searchBarClassNames?.selectContainer ?? DEFAULT_SEARCH_BAR_CLASS_NAMES.selectContainer,
    searchBarContainer: searchBarClassNames?.searchBarContainer ?? DEFAULT_SEARCH_BAR_CLASS_NAMES.searchBarContainer,
    searchBar: searchBarClassNames?.searchBar ?? DEFAULT_SEARCH_BAR_CLASS_NAMES.searchBar,
    input: searchBarClassNames?.input ?? DEFAULT_SEARCH_BAR_CLASS_NAMES.input
  };
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const blockedFilter = conditionedFilter && !filterUnlocked;

  const changeOpenStatusFunction = () => {
    if (blockedFilter) return;
    openSelectFunction(!openSelectStatus);
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
  };

  const getFilteredOptions = (options: IrawSelectOption[]): IrawSelectOption[] => {
    if (!showSearch || !searchText.trim()) {
      return options;
    }

    return options.filter(option => {
      const optionName = option.name.toLowerCase();
      const searchTerm = searchText.toLowerCase();

      if (optionName.includes(searchTerm)) {
        return true;
      }

      if (countryListAlias) {
        const countryAlias = aliasCountry(option.name);
        if (countryAlias && countryAlias.toLowerCase().includes(searchTerm)) {
          return true;
        }
      }

      return false;
    });
  };

  const aliasCountry = (code: string) => {
    const myCountry = countryListAlias?.find(element => element.code === code);
    if (myCountry) return myCountry.name;
    return code;
  };

  const formSelectOptionsUI = () => {
    const countryAlias = !!countryListAlias;

    const filteredOptions = getFilteredOptions(rawSelectOptions);

    const processedOptions = sortAlphabetically
      ? [...filteredOptions].sort((a, b) => {
          const nameA = (a.nameToSortAlphabetically ?? a.name)?.trim().toLowerCase();
          const nameB = (b.nameToSortAlphabetically ?? b.name)?.trim().toLowerCase();
          return nameA > nameB ? 1 : -1;
        })
      : filteredOptions;

    return processedOptions.reduce<JSX.Element[]>((prev, option, i) => {
      if (option?.name?.length > 0) {
        const newOption = (
          <div
            key={`${filterTitle}Option${i}`}
            className={`optionContainer ${optionContainerCustomClass}`.trim()}
            onClick={() => handleChangeSelectedOption(option)}
          >
            <div className={`optionText ${optionTextCustomClass}`}>
              {option.lineTitle && <div className="optionLineTitle">{option.lineTitle}</div>}
              <div className="optionValue ">
                {typeof option?.icon === 'string' && <img src={option?.icon} alt="option icon" />}
                {typeof option?.icon !== 'string' && React.isValidElement(option?.icon) && option?.icon}
                {countryAlias ? aliasCountry(option.name) : option.name}
              </div>
            </div>
            {useRadioButtons ? (
              <NudosRadioButton
                isButtonActive={option?.isActive}
                componentSize="small"
              />
            ) : (
              <NudosSelectButton
                iconName="xmark"
                componentSize="small"
                isButtonActive={option?.isActive}
                colorPalette="orange"
              />
            )}
          </div>
        );
        return [...prev, newOption];
      }
      return prev;
    }, []);
  };

  const filterClass = `${isLargeFilter ? 'isLargeFilter' : ''}`;
  const heightContainerClass = `${customHeightContainer ?? 'defaultHeightContainer'}`;
  const searchClass = `${showSearch ? 'hasSearch' : ''}`;
  const isLargeFillterClass = `${filterClass}  genericSelect ${heightContainerClass} ${searchClass}`;
  const selectedCount = rawSelectOptions?.filter(option => option?.isActive)?.length;
  const selectedCountText = selectedCount > 99 ? '99+' : selectedCount;
  const isShowBubbleInformation = showBubbleInformation && selectedCount > 0;

  return (
    <div className={`moduleFilterExpandableSelect ${isFilterActive ? 'active' : ''}`}>
      <div
        style={{ color: blockedFilter ? '#CCCCCC' : '' }}
        className={`closedSelect ${isShowBubbleInformation ? 'withBubbleInformation' : ''}`}
        onClick={() => changeOpenStatusFunction()}
      >
        {filterTitle}
      </div>
      {isShowBubbleInformation && (
        <div className="bubbleInformation">
          <div className="bubbleInformationText">{selectedCountText}</div>
        </div>
      )}
      {!openSelectStatus && (
        <div className="expandArrow" onClick={() => changeOpenStatusFunction()}>
          {showSortInFilter === filterTitle ? (
            <BordOneToneIcon className="showSortFilterIcon" variant="arrowUpCircle" standardSize={20} />
          ) : (
            <BordOneToneIcon stroke='#383838' variant="arrowHeadDown" standardSize={16} />
          )}
        </div>
      )}
      {openSelectStatus && (
        <div className="contractArrow" onClick={() => changeOpenStatusFunction()}>
          {showSortInFilter === filterTitle ? (
            <BordOneToneIcon className="showSortFilterIcon" variant="arrowDownCircle" standardSize={18} />
          ) : (
            <BordOneToneIcon stroke='#383838' variant="arrowHeadDown" standardSize={16} />
          )}
        </div>
      )}

      {openSelectStatus && (
        <div className="filtersAndSortContainer" onClick={e => e.stopPropagation()}>
          <div className={isLargeFillterClass}>
            {showSearch && (
              <div className={searchBarClasses.selectContainer}>
                <NudosSearchBar
                  handleChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  componentSize="small"
                  customSearchBarContainerClass={searchBarClasses.searchBarContainer}
                  customClassName={searchBarClasses.searchBar}
                  customInputClass={searchBarClasses.input}
                />
                <div className="lineSeparator"></div>
              </div>
            )}
            <div className="containerAbsolute">
              {titleFilterContainer && <div className="titleFilterContainer">{titleFilterContainer}</div>}
              {formSelectOptionsUI()}
            </div>
            {showSort && onSortChange && showSortInFilter === filterTitle && (
              <Sort
                onSortChange={onSortChange}
                showReferenceLetters={showReferenceLetters}
                customSortClassName={customSortClassName}
                currentSortFilter={currentSortFilter}
              />
            )}
          </div>
        </div>
      )}
      {aditionalInformation && (
        <div className="floatInformation" onMouseOver={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
          <div className="containerIcon">
            <InfoIcon fill={!showInfo ? '#383838' : ''} />
            {showInfo && (
              <div className="floatInformation">
                <div className="textInformation">{aditionalInformation}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ModuleFiltersExpandableSelect;

export interface IModuleFiltersExpandableSelect {
  filterTitle: string;
  openSelectStatus: boolean;
  openSelectFunction: React.Dispatch<React.SetStateAction<boolean>>;
  rawSelectOptions: IrawSelectOption[];
  handleChangeSelectedOption: (selectedOption: IrawSelectOption) => void;
  isFilterActive?: boolean;
  conditionedFilter?: boolean;
  filterUnlocked?: boolean;
  aditionalInformation?: string;
  countryListAlias?: ICountryDetail[];
  optionTextCustomClass?: string;
  showSort?: boolean;
  onSortChange?: (sortFilter: ISortFilter) => void;
  showSortInFilter?: string;
  customHeightContainer?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  sortAlphabetically?: boolean;
  isLargeFilter?: boolean;
  showReferenceLetters?: boolean;
  titleFilterContainer?: string;
  customSortClassName?: string;
  useRadioButtons?: boolean;
  currentSortFilter?: ISortFilter;
  searchBarClassNames?: ISearchBarClassNames;
  showBubbleInformation?: boolean;
  optionContainerCustomClass?: string;
}

export interface ISearchBarClassNames {
  selectContainer?: string;
  searchBarContainer?: string;
  searchBar?: string;
  input?: string;
}

export interface IrawSelectOption {
  name: string;
  value?: string;
  id?: string | number;
  icon?: string | React.ReactElement | null;
  isActive?: boolean;
  lineTitle?: string;
  nameToSortAlphabetically?: string;
  isLargeFilter?: boolean;
  titleFilterContainer?: string;
}
